import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useCallback, useRef, useState, createContext, useContext } from 'react';
import { TrainApiClient, defaultTrainSDK } from '@train-protocol/sdk';
import { defaultTrainAuth } from '@train-protocol/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TrainContext } from './TrainContext';
import { NetworksProvider } from './NetworksProvider';
import { SecretDerivationProvider } from './SecretDerivationProvider';
import { WalletContext } from '../wallet/WalletContext';
import { createSwapStore } from '../internal/store';
import { parseCaip2Id } from '../internal/branded';
const StoreContext = createContext(null);
export function useStoreContext() {
    return useContext(StoreContext);
}
export function TrainProvider({ children, ...config }) {
    // Create API client (stable across renders)
    const apiClient = useMemo(() => new TrainApiClient({ baseUrl: config.baseUrl }), [config.baseUrl]);
    // Use provided SDK/Auth instances or fall back to defaults
    const sdk = config.sdk ?? defaultTrainSDK;
    const auth = config.auth ?? defaultTrainAuth;
    // Create swap store (stable across renders)
    const storeRef = useRef(null);
    if (!storeRef.current) {
        storeRef.current = createSwapStore({
            persist: config.persistSwaps !== false,
            storage: config.storage,
        });
    }
    // Create or use provided QueryClient (stable across renders)
    const queryClientRef = useRef(null);
    if (!queryClientRef.current) {
        queryClientRef.current = config.queryClient ?? new QueryClient({
            defaultOptions: {
                queries: {
                    staleTime: 30000,
                    retry: 1,
                    refetchOnWindowFocus: false,
                },
            },
        });
    }
    // Wallet adapter registry — adapterVersion triggers re-render when adapters change,
    // so consumers whose useMemo depends on walletCtx can retry after initial "no adapter" failures.
    const adaptersRef = useRef(new Map());
    const [adapterVersion, setAdapterVersion] = useState(0);
    const registerAdapter = useCallback((adapter) => {
        adaptersRef.current.set(adapter.chainNamespace, adapter);
        setAdapterVersion(v => v + 1);
        return () => {
            adaptersRef.current.delete(adapter.chainNamespace);
            setAdapterVersion(v => v + 1);
        };
    }, []);
    const findAdapter = useCallback((networkId) => {
        const { namespace } = parseCaip2Id(networkId);
        const adapter = adaptersRef.current.get(namespace);
        if (!adapter) {
            throw new Error(`No wallet adapter registered for namespace "${namespace}" (network: "${networkId}"). ` +
                `Did you forget to call useRegisterWallet()?`);
        }
        return adapter;
    }, []);
    // adapterVersion in deps ensures these callbacks get new references when adapters register,
    // which propagates through walletValue → walletCtx → consumer useMemos.
    const createClient = useCallback((networkId) => {
        return findAdapter(networkId).createClient(sdk, networkId);
    }, [findAdapter, sdk, adapterVersion]);
    const createWriteClient = useCallback((networkId, address) => {
        return findAdapter(networkId).createWriteClient(sdk, networkId, address);
    }, [findAdapter, sdk, adapterVersion]);
    const getLoginConfig = useCallback(async (namespace, address) => {
        const adapter = adaptersRef.current.get(namespace);
        return (await adapter?.getLoginConfig?.(address)) ?? null;
    }, []);
    const walletValue = useMemo(() => ({ registerAdapter, createClient, createWriteClient, getLoginConfig }), [registerAdapter, createClient, createWriteClient, getLoginConfig]);
    // Memoize a stable config object to prevent downstream re-renders
    const stableConfig = useMemo(() => ({
        baseUrl: config.baseUrl,
        onError: config.onError,
        resolveNodeUrls: config.resolveNodeUrls,
        resolveLightClient: config.resolveLightClient,
        lightClientMinAmountUsd: config.lightClientMinAmountUsd,
        persistSwaps: config.persistSwaps,
        storage: config.storage,
        sdk: config.sdk,
        auth: config.auth,
        queryClient: config.queryClient,
        initialNetworks: config.initialNetworks,
        initialPrices: config.initialPrices,
        secretDerivation: config.secretDerivation,
    }), [config.baseUrl, config.onError, config.resolveNodeUrls, config.resolveLightClient, config.lightClientMinAmountUsd, config.persistSwaps, config.storage, config.sdk, config.auth, config.queryClient, config.initialNetworks, config.initialPrices, config.secretDerivation]);
    const trainValue = useMemo(() => ({ apiClient, config: stableConfig, sdk, auth }), [apiClient, stableConfig, sdk, auth]);
    return (_jsx(QueryClientProvider, { client: queryClientRef.current, children: _jsx(TrainContext.Provider, { value: trainValue, children: _jsx(WalletContext.Provider, { value: walletValue, children: _jsx(StoreContext.Provider, { value: storeRef.current, children: _jsx(NetworksProvider, { initialNetworks: stableConfig.initialNetworks, initialPrices: stableConfig.initialPrices, children: _jsx(SecretDerivationProvider, { ...stableConfig.secretDerivation, children: children }) }) }) }) }) }));
}
